import { Injectable } from '@angular/core';
import { AuthHttpService } from './authentication/auth-http.service';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { DataService } from './data.service';
import { FarmInfo } from '../shared/models/app.viewmodels';
import { Cacheable } from 'ts-cacheable';
import { User } from 'SharedModels/app.models';
import { CROP_INSURANCE_PRODUCT_ID } from 'app/app.constants';


const modeOfArray = (arr): number => {
  const numMapping = {};
  let greatestFreq = 0;
  let mode = null;
  arr.forEach(function findMode(number) {
    numMapping[number] = (numMapping[number] || 0) + 1;
    if (greatestFreq < numMapping[number]) {
      greatestFreq = numMapping[number];
      mode = number;
    }
  });
  return mode;
};


@Injectable()
export class FarmsService {

  constructor(private _authHttp: AuthHttpService, private _dataService: DataService) { }

  @Cacheable()
  getClientFarms(clientId: number): Observable<FarmInfo> {
    // If there are no crop types in the data service then fetch them
    if (this._dataService.cropTypes) {
      return this._getFarmAccessClientFarms(clientId);
    } else {
      return this._dataService.getCropTypes().pipe(
        switchMap(() => {
          return this._getFarmAccessClientFarms(clientId);
        })
      );
    }
  }

  private _getFarmAccessClientFarms(clientId: number) {
    return this._authHttp.get(`gateway/farmaccess/get_farms/?client_id=${clientId}`).pipe(
      tap((farmInfo: FarmInfo) => {
        this._dataService.farmInfo = farmInfo;
        this._formatFarms();
      })
    );
  }

  @Cacheable({
    maxCacheCount: Infinity
  })
  /** Function to ensure that client users don't access crop insurance **/
  isNotAnInsuranceFarmOrIsStaffUser(isStaff: boolean, farmId: string): Observable<boolean> {
    // Make sure the farm has the correct farm and client id
    return this._authHttp.get(`gateway/farms/${farmId}/product_orchards/`).pipe(
      map((product_access: any[]) => {
        const notAnInsuranceFarm = !product_access.find(_ => _.product_id === CROP_INSURANCE_PRODUCT_ID);
        return notAnInsuranceFarm || isStaff;
      }),
      catchError(() => {
        return of(false);
      })
    );
  }

  private _formatFarms() {
    this._dataService.farmInfo.farms.forEach(clientFarm => {
      clientFarm.orchards = this._dataService.farmInfo.orchards.filter(_ => _.farm_id === clientFarm.farm_id);
      clientFarm.total_hectares = clientFarm.orchards.map(_ => _.hectares).reduce((a, b) => a + b, 0);
      clientFarm.shared_with_clients = this._dataService.farmInfo.farms_shared_with.filter(_ => _.farm_id === clientFarm.farm_id);

      const mostFrequentCropType = modeOfArray(clientFarm.orchards.map(_ => _.crop_type_id).filter(_ => _ !== null));
      const mostFrequentCrop = this._dataService.cropTypes.find(_ => _.id === mostFrequentCropType);
      // Default crop family id to 10 "Other"
      let cropFamilyId = 10;
      if (mostFrequentCrop && mostFrequentCrop.crop_family) {
        cropFamilyId = mostFrequentCrop.crop_family.id;
      }
      clientFarm.crop_family_id = cropFamilyId;

    });
    this._dataService.clientFarms = this._dataService.farmInfo.farms
      .sort((a, b) => a.farm_name > b.farm_name ? 1 : -1);
  }

  public getNonPolicyFarmIdsForInsuranceUser(): void {
    // Get all farms that are not part of a policy that a user has access to (farms and farms shared with)
    const policyGrowerFarmIds = [].concat(...this._dataService.policyInfo.policies.map(_ => _.grower_farm_ids));
    const clientFarmsNotInPolicy = this._dataService.farmInfo.farms.filter(_ => !policyGrowerFarmIds.includes(_.farm_id));
    const farmsSharedWithClientNotInPolicy = this._dataService.farmInfo.farms_shared_with
      .filter(_ => !policyGrowerFarmIds.includes(_.farm_id));
    this._dataService.nonPolicyFarmsForInsuranceUser = [...clientFarmsNotInPolicy, ...farmsSharedWithClientNotInPolicy];
  }

}
